/**
 * Money and dates, formatted the way this company writes them.
 *
 * Read from the active workspace row and from nowhere else. The single-tenant
 * product had one currency, one locale and one timezone spread across
 * twenty-five files, which is why making it multi-company meant touching every
 * one of them; `config/defaults.ts` supplies a default at the moment a company
 * is created and is never consulted again.
 *
 * `pnpm check:tenant` fails the build if a currency or locale literal appears
 * anywhere in the client outside that one config module, so a formatter built
 * from a constant cannot get past the gate.
 */
import { useMemo } from 'react';
import { createDateFormatter, createMoneyFormatter, type DateFormatter, type MoneyFormatter } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';

export interface Formatters {
    money: MoneyFormatter;
    date: DateFormatter;
    /** A whole number of accounts, days or cheques, grouped this company's way. */
    count: (value: number) => string;
    /**
     * The day the **company** is having, as a Date at local midnight.
     *
     * Every derived state that turns on a date — whether a follow-up is
     * overdue, whether a cheque is due today — must be worked out against
     * this and not against `new Date()`. The database derives them in the
     * company's timezone; a browser deriving them in its own would put a badge
     * saying "Overdue" on a row the database counted as "Due today", and the
     * two would be right about different days.
     *
     * At local midnight rather than the company's, because the domain rules
     * compare local midnights: the point is the calendar day, and this carries
     * the company's one into the browser's arithmetic.
     */
    today: Date;
}

export function useFormatters(): Formatters {
    const { workspace } = useActiveWorkspace();

    return useMemo(() => {
        // An invalid locale or currency on the row must not blank every figure
        // on the screen. A company that typed something odd into settings gets
        // plain numbers, not an error boundary.
        const safe = <T,>(build: () => T, fallback: () => T): T => {
            try {
                return build();
            } catch {
                return fallback();
            }
        };

        const money = safe(
            () => createMoneyFormatter({ locale: workspace.locale, currency: workspace.currency }),
            () => createMoneyFormatter({ locale: 'en', currency: 'USD' }),
        );
        // The company's timezone, not the browser's. A cheque dated the 1st is
        // dated the 1st wherever the person reading it is sitting, and seeding
        // a date input from the browser's UTC day is how one recorded at 9am in
        // Mumbai came back dated the day before.
        const date = safe(
            () => createDateFormatter(workspace.locale, workspace.timezone),
            () => createDateFormatter('en'),
        );
        const grouping = safe(
            () => new Intl.NumberFormat(workspace.locale, { maximumFractionDigits: 0 }),
            () => new Intl.NumberFormat('en', { maximumFractionDigits: 0 }),
        );

        const today = (() => {
            const [year, month, day] = date.todayValue().split('-').map(Number);
            const local = new Date();
            if (year && month && day) local.setFullYear(year, month - 1, day);
            local.setHours(0, 0, 0, 0);
            return local;
        })();

        return { money, date, today, count: (value: number) => grouping.format(value) };
    }, [workspace.locale, workspace.currency, workspace.timezone]);
}
